// services/BackgroundRefreshService.js
import BrandDataManager from '../utils/BrandDataManager';

/**
 * Background refresh service for automatic cache updates
 */
class BackgroundRefreshService {
  constructor() {
    this.refreshInterval = null;
    this.refreshIntervalMs = 5 * 60 * 1000; // 5 minutes
    this.isRefreshing = false;
    this.listeners = new Set();
    this.lastRefreshTime = null;
    this.refreshCount = 0;
    this.errorCount = 0;
  }

  /**
   * Start background refresh service
   */
  start() {
    if (this.refreshInterval) {
      console.log('Background refresh service already running');
      return;
    }

    console.log('Starting background refresh service');
    
    // Initial refresh if cache is stale
    this.checkAndRefresh();

    // Set up periodic refresh
    this.refreshInterval = setInterval(() => {
      this.checkAndRefresh();
    }, this.refreshIntervalMs);

    // Listen for visibility changes to refresh when tab becomes active
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  /**
   * Stop background refresh service
   */
  stop() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('Background refresh service stopped');
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  /**
   * Check if refresh is needed and perform it
   */
  async checkAndRefresh() {
    if (this.isRefreshing) {
      console.log('Refresh already in progress, skipping');
      return;
    }

    // Check if cache is still valid
    if (BrandDataManager.isCacheValid()) {
      console.log('Cache is still valid, skipping refresh');
      return;
    }

    console.log('Cache is stale, performing background refresh');
    await this.performRefresh();
  }

  /**
   * Perform the actual refresh
   */
  async performRefresh() {
    if (this.isRefreshing) return;

    this.isRefreshing = true;
    this.notifyListeners('refreshStart');

    try {
      console.log('Starting background refresh...');
      const startTime = performance.now();

      const freshData = await BrandDataManager.refreshCache();
      
      if (freshData) {
        this.lastRefreshTime = new Date();
        this.refreshCount++;
        this.errorCount = 0; // Reset error count on success
        
        const endTime = performance.now();
        console.log(`Background refresh completed in ${(endTime - startTime).toFixed(2)}ms`);
        
        this.notifyListeners('refreshSuccess', {
          data: freshData,
          refreshTime: this.lastRefreshTime,
          duration: endTime - startTime
        });
      } else {
        throw new Error('No data returned from refresh');
      }
    } catch (error) {
      this.errorCount++;
      console.warn('Background refresh failed:', error);
      
      this.notifyListeners('refreshError', {
        error,
        errorCount: this.errorCount
      });

      // Implement exponential backoff for errors
      if (this.errorCount > 3) {
        console.log('Too many refresh errors, increasing interval');
        this.adjustRefreshInterval(this.refreshIntervalMs * 2);
      }
    } finally {
      this.isRefreshing = false;
      this.notifyListeners('refreshEnd');
    }
  }

  /**
   * Handle visibility change (tab focus/blur)
   */
  handleVisibilityChange = () => {
    if (!document.hidden) {
      console.log('Tab became visible, checking for refresh');
      // Refresh if cache is older than 2 minutes when tab becomes active
      const cacheAge = Date.now() - (BrandDataManager.cache.timestamp || 0);
      if (cacheAge > 2 * 60 * 1000) {
        this.performRefresh();
      }
    }
  };

  /**
   * Handle online event
   */
  handleOnline = () => {
    console.log('Connection restored, performing refresh');
    this.performRefresh();
  };

  /**
   * Handle offline event
   */
  handleOffline = () => {
    console.log('Connection lost, background refresh will pause');
  };

  /**
   * Adjust refresh interval
   */
  adjustRefreshInterval(newInterval) {
    const maxInterval = 30 * 60 * 1000; // 30 minutes max
    const minInterval = 1 * 60 * 1000; // 1 minute min
    
    this.refreshIntervalMs = Math.min(Math.max(newInterval, minInterval), maxInterval);
    
    if (this.refreshInterval) {
      this.stop();
      this.start();
    }
    
    console.log(`Refresh interval adjusted to ${this.refreshIntervalMs / 1000}s`);
  }

  /**
   * Add listener for refresh events
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data = null) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.warn('Error in refresh listener:', error);
      }
    });
  }

  /**
   * Force immediate refresh
   */
  async forceRefresh() {
    console.log('Force refresh requested');
    await this.performRefresh();
  }

  /**
   * Get service status
   */
  getStatus() {
    return {
      isRunning: !!this.refreshInterval,
      isRefreshing: this.isRefreshing,
      refreshInterval: this.refreshIntervalMs,
      lastRefreshTime: this.lastRefreshTime,
      refreshCount: this.refreshCount,
      errorCount: this.errorCount,
      cacheStatus: BrandDataManager.getCacheStatus()
    };
  }

  /**
   * Reset error count and restore normal interval
   */
  resetErrors() {
    this.errorCount = 0;
    this.adjustRefreshInterval(5 * 60 * 1000); // Reset to 5 minutes
  }
}

// Create singleton instance
const backgroundRefreshService = new BackgroundRefreshService();

export default backgroundRefreshService;